import type { ChatConversationSummary } from '@/types/chat';

export type ChatNotificationGet = (path: string) => Promise<{ data: unknown }>;

const PAGE_SIZE = 100;
const MAX_PAGES = 50;

const recordValue = (value: unknown): Record<string, unknown> | null =>
	typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;

const summaryFrom = (value: unknown): ChatConversationSummary => {
	const summary = recordValue(value);
	if (
		!summary ||
		typeof summary.id !== 'string' ||
		!summary.id ||
		typeof summary.unreadCount !== 'number' ||
		!Number.isFinite(summary.unreadCount)
	) {
		throw new Error('Chat notifications returned an invalid notification summary.');
	}
	return summary as unknown as ChatConversationSummary;
};

const pageFrom = (
	value: unknown
): { items: ChatConversationSummary[]; nextCursor?: string } => {
	if (Array.isArray(value)) return { items: value.map(summaryFrom) };
	const page = recordValue(value);
	if (!page || !Array.isArray(page.items)) {
		throw new Error('Chat notifications returned an invalid notification page.');
	}
	if (
		page.nextCursor !== undefined &&
		page.nextCursor !== null &&
		typeof page.nextCursor !== 'string'
	) {
		throw new Error('Chat notifications returned an invalid pagination cursor.');
	}
	const nextCursor =
		typeof page.nextCursor === 'string' && page.nextCursor.length > 0
			? page.nextCursor
			: undefined;
	return {
		items: page.items.map(summaryFrom),
		...(nextCursor ? { nextCursor } : {})
	};
};

export const loadAllChatConversationSummaries = async (
	get: ChatNotificationGet
): Promise<ChatConversationSummary[]> => {
	const summaries: ChatConversationSummary[] = [];
	const seenCursors = new Set<string>();
	let cursor: string | undefined;

	for (let pageNumber = 0; pageNumber < MAX_PAGES; pageNumber += 1) {
		const path = `api/chat/notifications?limit=${PAGE_SIZE}${
			cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''
		}`;
		// Cursor pages are ordered; requesting the next page requires the cursor from this response.
		// eslint-disable-next-line no-await-in-loop
		const page = pageFrom((await get(path)).data);
		summaries.push(...page.items);
		if (!page.nextCursor) return summaries;
		if (seenCursors.has(page.nextCursor)) {
			throw new Error('Chat notifications returned a repeated pagination cursor.');
		}
		seenCursors.add(page.nextCursor);
		cursor = page.nextCursor;
	}

	throw new Error('Chat notifications exceeded the supported pagination limit.');
};
